/**
 * Settings, laid out as the AI Workbench's: a column of grouped section links beside the content (a
 * picker on a narrow window), an Overview to start from, and each section a header then calm groups
 * of rows (components/ui/Layout.tsx). Every section has its own address, `/settings/<section>`.
 *
 *   You           everyone: their account and sign-ins, linked accounts and subscriptions,
 *                 notifications, phone approvals, away, date and time, appearance
 *   Organisation  administrators (on Cloud, members see the Organisation page alone): the
 *                 organisation, people and permissions, licence, subscriptions, data and encryption,
 *                 audit, integrations, the readiness report, and this platform (Enterprise)
 *   About         everyone: acknowledgements
 *
 * Old addresses still land: `/settings/you` and `?tab=` go to the right page, `/settings/attributions`
 * to Acknowledgements, and the way back from linking an account (`#linked-accounts`, `?github_link=`,
 * `?linked=`, `?link_failed=`, which the API and workbenches send to `/settings`) to Linked accounts.
 * Memory access lives on the Memory view and restore points on Backups; Settings points there.
 */

import { quotaAmountText, type A2aSettings, type TenantExport } from '@aioe/shared';
import { dateTime } from '@aioe/shared/wording/dateTime';
import { Bell, Briefcase, ChartColumn, CircleInfo, Clock, Database, FileText, Gear, HandStop, Key, Link as LinkIcon, Palette, Person, PersonGear, PlugConnection, SealCheck, Server, Smartphone, Persons } from '@gravity-ui/icons';
import { SearchField, Sheet } from '@heroui/react';
import { Segment } from '@heroui-pro/react';
import { type ReactNode, useEffect, useState, useSyncExternalStore } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';

import { API_URL, organisationExportRoutes, organisationKeysRoutes, problemText, routes, type OrganisationKeysView } from '../api/client.ts';
import { useAuth } from '../auth/AuthProvider.tsx';
import { useCloudStanding, type CloudStanding } from '../cloudPlan.ts';
import { BarePanels, Button, ConfirmDialog, ErrorNote, ListBlock, ListGroup, ListRow, PageColumn, PageHeader, StatusMark, Toggle } from '../components/ui.tsx';
import { Explainer, SubnavLayout, type SubnavGroup } from '../components/ui/Layout.tsx';
import { ReadinessPanel } from '../components/ReadinessPanel.tsx';
import { RolesPermissionsPanel } from '../components/rolesPermissions.tsx';
import { LicencePanel } from '../components/LicencePanel.tsx';
import { Agent365Panel } from '../components/Agent365Panel.tsx';
import { Microsoft365GroundingPanel } from '../components/Microsoft365GroundingPanel.tsx';
import { A2aSettingsPanel } from '../components/a2aAgents.tsx';
import { SentinelPanel } from '../components/SentinelPanel.tsx';
import { PurviewPanel } from '../components/PurviewPanel.tsx';
import { VIEW_TONES } from '../components/ui/Art.tsx';
import { useLoad } from '../hooks.ts';
import { useMay } from '../permissions.ts';
import { LINKED_ACCOUNTS_ANCHOR, SourceConnectionsPanel, SourceIdentitiesPanel } from '../components/source.tsx';
import { OrganisationProfilePanel } from '../components/organisationProfile.tsx';
import { CurrencyPanel } from '../components/currency.tsx';
import { PushSettingsPanel } from '../components/pushSettings.tsx';
import { ProjectRegistrationPanel } from '../components/projectRegistration.tsx';
import { MyRemoteApprovalsPanel } from '../components/remoteApprovals.tsx';
import { NotificationPreferencesPanel } from '../components/notificationPreferences.tsx';
import { AvailabilityPanel } from '../components/availability.tsx';
import { DateTimePanel } from '../components/dateTimeSettings.tsx';
import { MySubscriptionsPanel, SubscriptionsAdmin } from '../components/subscriptions.tsx';
import { CloudOrganisationPanel, CloudProfilePanel, DeleteAccountPanel, SignInMethodsPanel } from '../components/cloud/AccountPanels.tsx';
import { isCloud } from '../edition.ts';
import { PartBoundary } from '../components/ui/PartBoundary.tsx';
import { AttributionsPanel } from '../components/attributions.tsx';
import { AuditSettingsPanel } from '../components/auditSettings.tsx';
import { ThemeSwitch } from '../components/shell/ThemeSwitch.tsx';
import { consoleDesignSystem, subscribeConsoleDesignSystem } from '../orgDesignSystem.ts';
import { analyticsOffered, setUsageConsent, subscribeAnalytics, usageConsent } from '../analytics.ts';
import { currentAppearance, type DesignSystem, setDesignSystem, subscribeAppearance } from '../theme.ts';

const TONE = VIEW_TONES.settings!;

/* ---------- who is looking ---------- */

interface Viewer {
  cloud: boolean;
  /** Administrators (Enterprise: Manage on the organisation's settings; Cloud: owners and administrators). */
  admin: boolean;
  /** Cloud owners: the organisation's export is theirs. */
  owner: boolean;
  /** Whether `admin` is settled: a deep link to an administrator's page waits for it rather than bouncing. */
  known: boolean;
  standing: CloudStanding;
  roles: ReturnType<typeof useLoad<Awaited<ReturnType<typeof routes.roles>> | undefined>>;
}

/* ---------- the sections ---------- */

type GroupKey = '' | 'you' | 'organisation' | 'about';
const GROUP_LABEL: Record<GroupKey, string> = { '': '', you: 'You', organisation: 'Organisation', about: 'About' };

interface Section {
  key: string;
  label: string;
  group: GroupKey;
  icon: ReactNode;
  /** One sentence: the page header's blurb and the Overview row's description. */
  blurb: string;
  /** More words a search finds this page by. */
  words?: string;
  shown: (v: Viewer) => boolean;
  render: (v: Viewer) => ReactNode;
}

const everyone = () => true;
const admins = (v: Viewer) => v.admin;
const enterpriseAdmins = (v: Viewer) => !v.cloud && v.admin;

const SECTIONS: Section[] = [
  { key: 'overview', label: 'Overview', group: '', icon: <Gear />, blurb: 'Your own settings, and the organisation’s if you look after it.', shown: everyone, render: (v) => <Overview viewer={v} /> },
  {
    key: 'account',
    label: 'Account',
    group: 'you',
    icon: <Person />,
    blurb: 'Your name, the ways you sign in, where you are signed in, and deleting your account.',
    words: 'profile name email sign in sign-in methods sessions sign out delete account',
    shown: (v) => v.cloud,
    render: () => (
      <>
        <CloudProfilePanel />
        <SignInMethodsPanel />
        <DeleteAccountPanel />
      </>
    ),
  },
  {
    key: 'accounts',
    label: 'Linked accounts',
    group: 'you',
    icon: <LinkIcon />,
    blurb: 'Your source control accounts, your subscriptions, and your restore point.',
    words: 'github azure devops jira token link unlink subscription claude chatgpt copilot restore point backup',
    shown: everyone,
    render: () => (
      <>
        <SourceIdentitiesPanel />
        <MySubscriptionsPanel />
        <ListGroup id="restore-point" title="Restore point">
          <ListRow to="/backups" title="Your restore point" description="Sets up a new desktop the way your last one was. It is kept on Backups." />
        </ListGroup>
      </>
    ),
  },
  {
    key: 'notifications',
    label: 'Notifications',
    group: 'you',
    icon: <Bell />,
    blurb: 'The email digest, quiet hours and what counts as for you.',
    words: 'email digest quiet hours time zone updates phone',
    shown: everyone,
    render: () => <NotificationPreferencesPanel />,
  },
  {
    key: 'approvals',
    label: 'Phone approvals',
    group: 'you',
    icon: <Smartphone />,
    blurb: 'Where you may approve what an agent on one of your workbenches asks to do.',
    words: 'remote approvals risk phone machine',
    shown: (v) => !(v.standing.cloud && !v.standing.teams),
    render: () => <MyRemoteApprovalsPanel />,
  },
  { key: 'away', label: 'Away', group: 'you', icon: <HandStop />, blurb: 'Mark yourself away, so the people who cover you can answer for you.', words: 'leave holiday cover availability', shown: everyone, render: () => <AvailabilityPanel /> },
  { key: 'date-time', label: 'Date and time', group: 'you', icon: <Clock />, blurb: 'The clock, the date order and the first day of the week.', words: 'clock 12-hour 24-hour date order week', shown: everyone, render: () => <DateTimePanel /> },
  { key: 'appearance', label: 'Appearance', group: 'you', icon: <Palette />, blurb: 'Light or dark, and the look of the console in this browser.', words: 'colour mode dark light theme look solid glass display', shown: everyone, render: () => <AppearancePanel /> },
  { key: 'privacy', label: 'Privacy', group: 'you', icon: <ChartColumn />, blurb: 'Whether this browser counts which pages you use, to help improve the service.', words: 'usage statistics analytics cookies consent tracking', shown: (v) => v.cloud && analyticsOffered(), render: () => <PrivacyPanel /> },
  {
    key: 'organisation',
    label: 'Organisation',
    group: 'organisation',
    icon: <Briefcase />,
    blurb: 'What the organisation is, its currency, what its phones may show, and who registers new projects.',
    words: 'profile vision description currency exchange rate phone notifications plan quotas memory new projects register members',
    shown: (v) => v.cloud || v.admin,
    render: (v) => (
      <>
        {v.cloud ? <CloudOrganisationPanel /> : null}
        <OrganisationProfilePanel />
        <CurrencyPanel />
        <PushSettingsPanel />
        {/* A Free account is one person: nobody else to register projects. */}
        {v.standing.cloud && !v.standing.teams ? null : <ProjectRegistrationPanel />}
        <ListGroup id="organisation-elsewhere" title="Elsewhere">
          <ListRow to="/memory" title="Memory access" description="Who reads which of the organisation’s memory." />
          <ListRow to="/backups" title="Restore points" description="Everyone’s restore points and workbench backups." />
        </ListGroup>
      </>
    ),
  },
  {
    key: 'roles',
    label: 'Roles and permissions',
    group: 'organisation',
    icon: <Persons />,
    blurb: 'Every person and agent gets exactly the access their role needs.',
    words: 'people permissions roles operators administrators members viewers custom role assign group agent least privilege nodes portfolio memory',
    shown: admins,
    render: (v) => (
      <>
        <RolesPermissionsPanel />
        {v.cloud ? null : <LegacyPermissionsPanel roles={v.roles} />}
      </>
    ),
  },
  { key: 'licence', label: 'Licence', group: 'organisation', icon: <SealCheck />, blurb: 'Who this service is licensed to, what it allows and when it expires.', words: 'licence token seats expiry trial check in', shown: enterpriseAdmins, render: () => <LicencePanel /> },
  { key: 'subscriptions', label: 'Subscriptions', group: 'organisation', icon: <PersonGear />, blurb: 'Which of people’s own subscriptions the organisation’s machines may use, and who has signed in.', words: 'claude chatgpt copilot google nodes headless token', shown: admins, render: () => <SubscriptionsAdmin /> },
  {
    key: 'data',
    label: 'Data and encryption',
    group: 'organisation',
    icon: <Key />,
    blurb: 'Export everything the organisation keeps here, and the key its secrets are sealed with.',
    words: 'export zip encryption key rotate support access deletion certificates',
    shown: admins,
    render: (v) => (
      <>
        {v.cloud && !v.owner ? null : <OrganisationExportPanel />}
        {v.cloud ? <CloudEncryptionPanel /> : <EncryptionKeysPanel />}
        {v.cloud ? (
          <ListGroup id="support-access" title="Support access" description="Our support team opens your organisation's data only to help with a request you raised, for at most four hours, and every time is recorded.">
            <ListRow to="/platform-access" title="See each time" description="Who opened what, when, and the request it was for." />
          </ListGroup>
        ) : (
          <DeletionCertificatesPanel />
        )}
      </>
    ),
  },
  {
    key: 'audit',
    label: 'Audit',
    group: 'organisation',
    icon: <FileText />,
    blurb: 'What the audit trail keeps of where and how things were done, and for how long.',
    words: 'audit trail retention location address forwarding',
    shown: admins,
    render: () => (
      <>
        <AuditSettingsPanel />
        <ListGroup id="audit-elsewhere" title="Elsewhere">
          <ListRow to="/audit" title="The audit trail" description="The trail itself, its exports and where it is forwarded." />
        </ListGroup>
      </>
    ),
  },
  {
    key: 'integrations',
    label: 'Integrations',
    group: 'organisation',
    icon: <PlugConnection />,
    blurb: 'Source control and the services the organisation connects to.',
    words: 'github azure devops jira webhooks microsoft agent 365 purview sentinel copilot',
    shown: admins,
    render: (v) => (
      <>
        <SourceConnectionsPanel />
        {v.cloud ? null : <MicrosoftCard />}
      </>
    ),
  },
  { key: 'readiness', label: 'Readiness report', group: 'organisation', icon: <Database />, blurb: 'What the organisation has in place for AI, as a document to keep or hand to an auditor.', words: 'readiness report gaps audit download', shown: admins, render: () => <ReadinessPanel /> },
  {
    key: 'platform',
    label: 'Platform',
    group: 'organisation',
    icon: <Server />,
    blurb: 'How workbenches find this service, and its technical details.',
    words: 'dns discovery cname txt tenant api relay identity provider database isolation',
    shown: enterpriseAdmins,
    render: () => (
      <>
        <DiscoveryPanel />
        <TechnicalDetails />
      </>
    ),
  },
  { key: 'acknowledgements', label: 'Acknowledgements', group: 'about', icon: <CircleInfo />, blurb: 'The open-source work and the projects this product is built on.', words: 'licences open source packages credits attributions', shown: everyone, render: () => <AttributionsPanel /> },
];

const SECTION_BY_KEY = new Map(SECTIONS.map((s) => [s.key, s]));
const sectionPath = (key: string) => (key === 'overview' ? '/settings' : `/settings/${key}`);

/** The pages a search finds: by name, by the blurb, by the words each page carries. */
function searchSections(query: string, shown: Section[]): Section[] {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return [];
  return shown.filter((s) => s.key !== 'overview' && terms.every((t) => `${s.label} ${s.blurb} ${s.words ?? ''}`.toLowerCase().includes(t)));
}

/* ---------- Overview ---------- */

function Overview({ viewer }: { viewer: Viewer }) {
  const shown = SECTIONS.filter((s) => s.key !== 'overview' && s.shown(viewer));
  const groups = (['you', 'organisation', 'about'] as const).map((g) => ({ g, sections: shown.filter((s) => s.group === g) })).filter((x) => x.sections.length);
  return (
    <>
      <Explainer id="settings" tone={TONE} icon={<Gear />} title="How Settings works" summary="Yours first, then the organisation’s, each change kept as you make it">
        <p>
          Settings has two halves. You is about you alone: how you are reached, where you may approve an agent’s request, when you are away and how dates are written for you. Those follow you to your
          workbench and your phone.
        </p>
        <p>
          {viewer.admin
            ? 'Organisation is what you look after for everyone: the organisation’s profile and currency, who may do what, its integrations, its audit trail and its data. Only administrators see it.'
            : 'Your organisation’s administrators look after the rest: its integrations, its audit trail and who may do what.'}{' '}
          A switch or a choice is kept as soon as you make it; a form of several fields has one Save.
        </p>
      </Explainer>
      {groups.map(({ g, sections }) => (
        <ListGroup key={g} id={`overview-${g}`} title={GROUP_LABEL[g]}>
          {sections.map((s) => (
            <ListRow key={s.key} to={sectionPath(s.key)} icon={s.icon} title={s.label} description={s.blurb} />
          ))}
        </ListGroup>
      ))}
    </>
  );
}

/* ---------- Appearance ---------- */

const DESIGN_TEXT: Record<DesignSystem, string> = { solid: 'Solid', glass: 'Glass' };

/** Colour mode and look, kept in this browser. The account menu at the foot of the sidebar switches the same two. */
function AppearancePanel() {
  const designSystem = useSyncExternalStore(subscribeAppearance, () => currentAppearance().designSystem);
  const organisationLook = useSyncExternalStore(subscribeConsoleDesignSystem, () => consoleDesignSystem().name);
  return (
    <ListGroup id="appearance" description="Kept in this browser. Your account menu, under your name at the foot of the sidebar, switches the same two, and holds Sign out.">
      <ListRow
        title="Colour mode"
        description="Follow this computer, or always light or dark."
        control={
          <div className="w-36">
            <ThemeSwitch />
          </div>
        }
      />
      <ListRow
        title="Look"
        description={organisationLook ? `Wearing ${organisationLook}, your organisation's design system.` : 'Solid surfaces, or glass ones that let the page show through.'}
        control={
          <Segment aria-label="Look" size="sm" className="w-36" selectedKey={designSystem} onSelectionChange={(k) => setDesignSystem(String(k) as DesignSystem)}>
            {(['solid', 'glass'] as DesignSystem[]).map((d) => (
              <Segment.Item key={d} id={d} className="flex-1 justify-center">
                {DESIGN_TEXT[d]}
              </Segment.Item>
            ))}
          </Segment>
        }
      />
    </ListGroup>
  );
}

function PrivacyPanel() {
  const consent = useSyncExternalStore(subscribeAnalytics, usageConsent);
  return (
    <ListGroup id="privacy" description="Kept in this browser. Only which pages are opened is counted, never what is on them, what you write or who you are.">
      <ListRow
        title="Usage statistics"
        description="Count the pages you open, with Google Analytics, so we can see what to improve."
        control={<Toggle label="Usage statistics" checked={consent === 'granted'} onChange={(on) => setUsageConsent(on ? 'granted' : 'denied')} />}
      />
    </ListGroup>
  );
}

/* ---------- Roles and permissions: the earlier switches ---------- */

/** What each permission lets a role do, in words. */
const PERMISSION_TEXT: Record<string, string> = {
  'nodes:approve': 'Approve nodes, their surfaces and join keys',
  'portfolio:manage': 'Manage business areas and file projects under them',
  'memory:write': 'Write to the organisation’s memory',
};
/** Roles an administrator can grant permissions to; admin holds every one already. */
const GRANTABLE_ROLES = ['operator'];

/**
 * The earlier switches (`PUT /admin/v1/roles`): what operators may do beside their role. Kept until 1.0 (decision 10);
 * the roles above read them, so an operator's row shows what is switched on here. Administrators only.
 */
function LegacyPermissionsPanel({ roles }: { roles: Viewer['roles'] }) {
  const [error, setError] = useState<string>();
  if (!roles.data) return null;
  const { permissions, roles: granted } = roles.data;
  const toggle = async (role: string, perm: string) => {
    const current = granted[role] ?? [];
    const next = { ...granted, [role]: current.includes(perm) ? current.filter((p) => p !== perm) : [...current, perm] };
    delete next.admin;
    setError(undefined);
    try {
      await routes.saveRoles(next);
      roles.reload();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  return (
    <ListGroup id="permissions" title="Operators may also" description="Switches kept from earlier releases: operators hold what is switched on here, on top of their role. Administrators hold all of it.">
      {permissions.map((perm) =>
        GRANTABLE_ROLES.map((role) => {
          const words = PERMISSION_TEXT[perm] ?? perm;
          return <ListRow key={`${role}/${perm}`} title={words} control={<Toggle label={`Operators may ${words.replace(/^./, (c) => c.toLowerCase())}`} checked={(granted[role] ?? []).includes(perm)} onChange={() => void toggle(role, perm)} />} />;
        }),
      )}
      {error ? (
        <ListBlock>
          <ErrorNote error={error} />
        </ListBlock>
      ) : null}
    </ListGroup>
  );
}

/* ---------- the organisation's export, and certificates of deletion ---------- */

const when = (iso: string) => dateTime(iso, { date: 'medium' });
const EXPORT_STATE: Record<TenantExport['state'], { state: 'working' | 'connected' | 'error' | 'disabled'; label: string }> = {
  running: { state: 'working', label: 'Preparing' },
  ready: { state: 'connected', label: 'Ready' },
  failed: { state: 'error', label: 'Failed' },
  expired: { state: 'disabled', label: 'Removed' },
};

/** What an export holds, or why there is nothing to download, in one line. */
function exportDetail(e: TenantExport, keepDays: number): string {
  if (e.state === 'running') return 'Every table and asset is being written into the archive.';
  if (e.state === 'failed') return e.error ?? 'The export failed.';
  if (e.state === 'expired') return `Exports are kept ${keepDays} days.`;
  const s = e.summary;
  const parts = s ? [`${s.rows.toLocaleString('en-AU')} rows in ${s.tables} tables`, ...(s.objects ? [`${s.objects} ${s.objects === 1 ? 'asset' : 'assets'}`] : [])] : [];
  return [...parts, ...(e.size !== undefined ? [quotaAmountText('bytes', e.size)] : []), ...(e.expiresAt ? [`kept until ${when(e.expiresAt)}`] : [])].join(' · ');
}

/**
 * A zip of everything the organisation holds here, for its own records or to restore it.
 * Administrators on Enterprise, owners on Cloud. One action; each export is a row with its state in
 * words and, once ready, its download.
 */
function OrganisationExportPanel() {
  const list = useLoad(() => organisationExportRoutes.exports().catch(() => undefined), []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const running = list.data?.exports.some((e) => e.state === 'running') ?? false;
  const { reload } = list;
  useEffect(() => {
    if (!running) return;
    const t = setTimeout(reload, 3000);
    return () => clearTimeout(t);
  }, [running, list.data, reload]);
  if (!list.data) return null;
  const { exports, keepDays, complete } = list.data;
  const start = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await organisationExportRoutes.startExport();
      list.reload();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  const save = async (e: TenantExport) => {
    setError(undefined);
    try {
      await organisationExportRoutes.downloadExport(e.id, `organisation-export-${e.createdAt.slice(0, 10)}.zip`);
    } catch (err) {
      setError(problemText(err));
    }
  };
  return (
    <ListGroup
      id="export"
      title="Export this organisation"
      description={`A zip of everything this organisation keeps here: every record, table by table, and the files its design systems use, with a manifest that checks each one. Secrets stay sealed; the key that opens them is not in it. People’s workbench backups are listed, not copied. Each export is kept ${keepDays} days.${complete ? '' : ' This development server holds its data in memory, so its exports carry a few tables only.'}`}
    >
      <ListRow
        title="A new export"
        description={running ? 'One is being prepared; it appears below when it is ready.' : 'Takes a minute or two for a large organisation.'}
        control={
          <Button size="sm" variant="secondary" isPending={busy || running} isDisabled={running} onPress={() => void start()}>
            {running ? 'Preparing' : 'Export'}
          </Button>
        }
      />
      {exports.slice(0, 5).map((e) => (
        <ListRow
          key={e.id}
          title={
            <>
              {when(e.createdAt)}
              {e.requestedByName ? <span className="font-normal text-muted"> · {e.requestedByName}</span> : null}
            </>
          }
          description={exportDetail(e, keepDays)}
          control={
            <>
              <StatusMark state={EXPORT_STATE[e.state].state} label={EXPORT_STATE[e.state].label} />
              {e.state === 'ready' ? (
                <Button size="sm" variant="secondary" onPress={() => void save(e)}>
                  Download
                </Button>
              ) : null}
            </>
          }
        />
      ))}
      {error ? (
        <ListBlock>
          <ErrorNote error={error} />
        </ListBlock>
      ) : null}
    </ListGroup>
  );
}

/* ---------- the organisation's encryption key ---------- */

/** What wraps the organisation's data key, in words: this deployment's own master, or a Key Vault key by name. */
function keyHolder(v: OrganisationKeysView): { mark: string; sentence: string } {
  if (v.driver === 'azure-keyvault') {
    const m = /^https:\/\/([^.]+)\.[^/]+\/keys\/([^/]+)/.exec(v.masterKeyId ?? '');
    return { mark: 'Key Vault', sentence: m ? `Keys are wrapped by Azure Key Vault: the key ${m[2]} in the vault ${m[1]}.` : 'Keys are wrapped by Azure Key Vault.' };
  }
  if (v.driver === 'aws-kms') return { mark: 'AWS KMS', sentence: 'Keys are wrapped by AWS KMS.' };
  return { mark: 'Local master key', sentence: 'Keys are wrapped by this deployment’s own master key.' };
}

/** Who made a version, in words: the platform on first use, an operator with aioe-keys, or an administrator here. */
function madeBy(by: string | undefined): string {
  if (!by || by === 'platform') return 'made when the organisation first sealed a secret';
  if (by.startsWith('aioe-keys:')) return `made by ${by.slice('aioe-keys:'.length)} with aioe-keys`;
  return 'made by an administrator';
}

/**
 * Mojo Up AI Cloud: that the organisation's secrets are sealed with a key of its own, and when it last changed. Where
 * the master key lives and how it is rotated are Mojo Up's to look after, so none of that is shown here.
 */
function CloudEncryptionPanel() {
  const keys = useLoad(() => organisationKeysRoutes.view().catch(() => undefined), []);
  const v = keys.data;
  if (!v) return null;
  return (
    <ListGroup id="encryption" title="Encryption" description="Every secret your organisation keeps is encrypted with a key that belongs to your organisation alone; no other organisation's data opens with it. AI Cloud looks after the keys, including rotating them.">
      <ListRow title="Your organisation’s key" value={v.active ? (v.rotatedAt ? `Last changed ${when(v.rotatedAt)}` : `Made ${when(v.active.createdAt)}`) : 'Made the first time your organisation keeps a secret'} />
    </ListGroup>
  );
}

/**
 * The data key every secret here is sealed with (docs/encryption.md), what wraps it, and one action,
 * Rotate. Rotation makes a new version for new values; nothing already sealed moves until an operator
 * runs `aioe-keys reseal`. Administrators (the API refuses anyone else).
 */
function EncryptionKeysPanel() {
  const keys = useLoad(() => organisationKeysRoutes.view().catch(() => undefined), []);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string>();
  const v = keys.data;
  if (!v) return null;
  const rotate = async () => {
    setError(undefined);
    try {
      await organisationKeysRoutes.rotate();
      setConfirming(false);
      keys.reload();
    } catch (e) {
      setConfirming(false);
      setError(problemText(e));
    }
  };
  const holder = keyHolder(v);
  const earlier = v.active ? v.versions.filter((k) => k.version < v.active!.version).length : 0;
  return (
    <>
      <ListGroup id="encryption-keys" title="Encryption keys" description={v.configured ? `${holder.sentence} Every secret this organisation keeps is sealed with its own data key, which no other organisation’s values open.` : undefined}>
        {!v.configured ? (
          <ListBlock className="text-muted">{v.message ?? 'This deployment keeps no secrets, so the organisation has no key.'}</ListBlock>
        ) : (
          <>
            <ListRow title="Wrapped by" control={<StatusMark state="connected" label={holder.mark} />} />
            {v.active ? (
              <>
                <ListRow
                  title={
                    <>
                      Version <span className="tabular-nums">{v.active.version}</span>
                    </>
                  }
                  description={`${when(v.active.createdAt)}, ${madeBy(v.active.createdBy)}.${v.rotatedAt ? ` Last rotated ${when(v.rotatedAt)}.` : ' Never rotated.'}`}
                  control={
                    <Button size="sm" variant="ghost" onPress={() => setConfirming(true)}>
                      Rotate key
                    </Button>
                  }
                />
                <ListRow title="Earlier versions" description={earlier === 0 ? 'None.' : `${earlier} earlier ${earlier === 1 ? 'version still opens' : 'versions still open'} the values sealed before ${earlier === 1 ? 'it was' : 'they were'} replaced, until an operator re-seals them.`} />
              </>
            ) : (
              <ListRow
                title="No key yet"
                description="One is made the first time this organisation keeps a secret."
                control={
                  <Button size="sm" variant="ghost" onPress={() => setConfirming(true)}>
                    Rotate key
                  </Button>
                }
              />
            )}
          </>
        )}
        {error ? (
          <ListBlock>
            <ErrorNote error={error} />
          </ListBlock>
        ) : null}
      </ListGroup>
      <ConfirmDialog isOpen={confirming} title="Rotate the organisation’s key?" confirmLabel="Rotate key" onConfirm={rotate} onCancel={() => setConfirming(false)}>
        <p>A new version of the data key seals every secret kept from now on. The current version is retired but kept, so everything it sealed still opens.</p>
        <p>Nothing already stored is re-sealed until an operator runs aioe-keys reseal on the deployment.</p>
      </ConfirmDialog>
    </>
  );
}

/**
 * The certificates of deletion this administrator asked for (an organisation the operator deleted on
 * their word): shown only when there are some. Each downloads as the signed JSON or as words.
 */
function DeletionCertificatesPanel() {
  const list = useLoad(() => organisationExportRoutes.deletionCertificates().catch(() => undefined), []);
  const [error, setError] = useState<string>();
  const certificates = list.data?.certificates ?? [];
  if (!certificates.length) return null;
  const save = async (id: string, format: 'json' | 'text') => {
    setError(undefined);
    try {
      await organisationExportRoutes.downloadDeletionCertificate(id, format);
    } catch (e) {
      setError(problemText(e));
    }
  };
  return (
    <ListGroup id="deletion-certificates" title="Certificates of deletion" description="What was deleted with each organisation you asked to have deleted, and what remains, signed by this platform so anyone can check it.">
      {certificates.map((c) => (
        <ListRow
          key={c.id}
          title={c.organisation}
          description={`Deleted ${when(c.completedAt)}`}
          control={
            <>
              <Button size="sm" variant="secondary" onPress={() => void save(c.id, 'json')}>
                Signed copy
              </Button>
              <Button size="sm" variant="ghost" onPress={() => void save(c.id, 'text')}>
                As text
              </Button>
            </>
          }
        />
      ))}
      {error ? (
        <ListBlock>
          <ErrorNote error={error} />
        </ListBlock>
      ) : null}
    </ListGroup>
  );
}

/* ---------- Microsoft, as one group ---------- */

type MicrosoftKey = 'agent365' | 'purview' | 'sentinel' | 'grounding' | 'a2a';
const MICROSOFT: Record<MicrosoftKey, { title: string; blurb: string; panel: () => ReactNode }> = {
  agent365: { title: 'Agent 365', blurb: 'Agent identities, observability and orders for your teams’ agents.', panel: () => <Agent365Panel /> },
  purview: { title: 'Purview', blurb: 'Sensitivity labels, and how they map to this organisation’s levels.', panel: () => <PurviewPanel /> },
  sentinel: { title: 'Sentinel', blurb: 'Security events sent to your Microsoft Sentinel workspace.', panel: () => <SentinelPanel /> },
  grounding: { title: 'Microsoft 365 Answers', blurb: 'Questions put to the memory are also put to Microsoft 365 for that person.', panel: () => <Microsoft365GroundingPanel /> },
  a2a: { title: 'Copilot Agents', blurb: 'Who may make a project’s agents callable from Microsoft 365 Copilot.', panel: () => <A2aSettingsPanel /> },
};

function MicrosoftCard() {
  const agent365 = useLoad(() => routes.agent365().catch(() => undefined), []);
  const purview = useLoad(() => routes.purview().catch(() => undefined), []);
  const sentinel = useLoad(() => routes.sentinel().catch(() => undefined), []);
  const grounding = useLoad(() => routes.microsoft365Grounding().catch(() => undefined), []);
  const a2a = useLoad(() => routes.a2aSettings().catch(() => undefined), []);
  const [open, setOpen] = useState<MicrosoftKey>();
  const status = (k: MicrosoftKey): { state: 'connected' | 'idle' | 'error'; label: string } | undefined => {
    switch (k) {
      case 'agent365':
        return agent365.data ? (agent365.data.connected ? { state: 'connected', label: 'Connected' } : { state: 'idle', label: 'Not set up' }) : undefined;
      case 'purview':
        return purview.data ? (purview.data.lastSyncError ? { state: 'error', label: 'Needs attention' } : purview.data.connected ? { state: 'connected', label: 'Connected' } : { state: 'idle', label: 'Not set up' }) : undefined;
      case 'sentinel':
        return sentinel.data ? (sentinel.data.lastError ? { state: 'error', label: 'Needs attention' } : sentinel.data.connected ? { state: 'connected', label: 'Connected' } : { state: 'idle', label: 'Not set up' }) : undefined;
      case 'grounding':
        return grounding.data ? (grounding.data.enabled ? { state: 'connected', label: 'On' } : { state: 'idle', label: grounding.data.ready ? 'Off' : 'Not set up' }) : undefined;
      case 'a2a':
        return a2a.data ? { state: 'connected', label: (a2a.data as A2aSettings).whoMayExpose === 'admins' ? 'Administrators only' : 'Administrators and owners' } : undefined;
    }
  };
  const reload = () => {
    agent365.reload();
    purview.reload();
    sentinel.reload();
    grounding.reload();
    a2a.reload();
  };
  const keys = Object.keys(MICROSOFT) as MicrosoftKey[];
  return (
    <>
      <ListGroup id="microsoft" title="Microsoft" description="Each service opens its own settings beside the page.">
        {keys.map((k) => {
          const s = status(k);
          return (
            <ListRow
              key={k}
              onPress={() => setOpen(k)}
              title={MICROSOFT[k].title}
              description={MICROSOFT[k].blurb}
              trailing={!s ? <span className="text-muted">…</span> : k === 'a2a' ? <span className="text-muted">{s.label}</span> : <StatusMark state={s.state} label={s.label} />}
            />
          );
        })}
      </ListGroup>
      <Sheet
        isOpen={open !== undefined}
        placement="right"
        onOpenChange={(o) => {
          if (!o) {
            setOpen(undefined);
            reload();
          }
        }}
      >
        <Sheet.Backdrop>
          <Sheet.Content className="w-full max-w-xl">
            <Sheet.Dialog aria-label={open ? `Microsoft ${MICROSOFT[open].title}` : 'Microsoft'}>
              <Sheet.Header>
                <Sheet.Heading>{open ? `Microsoft ${MICROSOFT[open].title}` : ''}</Sheet.Heading>
              </Sheet.Header>
              <Sheet.Body className="overflow-y-auto">
                <BarePanels.Provider value>{open ? MICROSOFT[open].panel() : null}</BarePanels.Provider>
              </Sheet.Body>
              <Sheet.CloseTrigger />
            </Sheet.Dialog>
          </Sheet.Content>
        </Sheet.Backdrop>
      </Sheet>
    </>
  );
}

/* ---------- Platform ---------- */

function DiscoveryPanel() {
  const { account } = useAuth();
  const host = new URL(API_URL).hostname;
  const domain = account?.email?.split('@')[1] ?? '<your-domain>';
  return (
    <ListGroup id="discovery" title="Connect workbenches" description="Workbenches find this service from the domain of the person’s email. Publish one of these records in DNS for each email domain, once.">
      <ListBlock>
        <pre className="mono overflow-x-auto rounded-lg bg-surface-secondary p-3 text-xs">{`aioe-discover.${domain}.  CNAME  ${host}.\n; or\naioe-discover.${domain}.  TXT    "v=aioe1; url=${API_URL}"`}</pre>
      </ListBlock>
      <ListRow to="/enrol" title="Add a workbench" description={`Once the record resolves, a workbench signing in with an address at ${domain} finds this organisation by itself.`} />
    </ListGroup>
  );
}

/** The database's isolation between organisations, in words: row-level security's mode (docs/architecture.md). */
function DatabaseIsolation() {
  const db = useLoad(() => routes.database().catch(() => undefined), []);
  const d = db.data;
  if (!d) return <span className="text-muted">{db.loading ? '…' : 'Not known'}</span>;
  if (d.store === 'memory') return <span className="text-muted">In-memory store: nothing to isolate</span>;
  const { mode, reports } = d.rowLevelSecurity;
  if (mode === 'enforce') return <StatusMark state="connected" label="Row-level security enforced" />;
  if (mode === 'off') return <StatusMark state="disabled" label="Row-level security off" />;
  return (
    <span className="inline-flex flex-wrap items-center gap-x-2">
      <StatusMark state={reports ? 'medium' : 'low'} label="Row-level security reporting" />
      <span className="text-xs text-muted">{reports ? `${reports} ${reports === 1 ? 'query' : 'queries'} found without an organisation since the last restart, in the log` : 'no query without an organisation since the last restart'}</span>
    </span>
  );
}

function TechnicalDetails() {
  const { discovery } = useAuth();
  const mono = (value: ReactNode) => <span className="mono break-all text-xs text-foreground">{value}</span>;
  return (
    <ListGroup id="technical" title="Technical details" description="For whoever runs this deployment.">
      <ListRow title="Organisation" value={discovery?.organisation} />
      <ListRow title="Tenant" value={mono(discovery?.tenant)} />
      <ListRow title="API" value={mono(API_URL)} />
      <ListRow title="Relay" value={mono(discovery?.relayUrl)} />
      <ListRow title="Identity provider" value={mono(discovery?.auth.issuer)} />
      <ListRow title="API scope" value={mono(discovery?.auth.apiScope)} />
      <ListRow title="Capabilities" description={discovery?.capabilities.join(', ')} />
      <ListRow title="Database isolation" control={<DatabaseIsolation />} />
    </ListGroup>
  );
}

/* ---------- the page ---------- */

/** Where an old address meant to go, or undefined when it is a page of today's. */
function oldAddress(tab: string | undefined, search: string, hash: string): string | undefined {
  const params = new URLSearchParams(search);
  const queryTab = params.get('tab');
  if (!tab && queryTab) {
    params.delete('tab');
    const rest = params.toString();
    return `${sectionPath(queryTab === 'you' ? 'overview' : queryTab === 'attributions' ? 'acknowledgements' : queryTab)}${rest ? `?${rest}` : ''}${hash}`;
  }
  // The way back from linking an account, which the API and workbenches send to /settings.
  if (!tab && (hash === `#${LINKED_ACCOUNTS_ANCHOR}` || params.has('github_link') || params.has('linked') || params.has('link_failed'))) return `/settings/accounts${search}${hash}`;
  if (tab === 'you') return `/settings${search}${hash}`;
  if (tab === 'people') return `/settings/roles${search}${hash}`;
  if (tab === 'attributions') return '/settings/acknowledgements';
  return undefined;
}

/** Whether this person was an administrator the last time Settings asked (Enterprise), for the next page's first render. */
let lastEnterpriseAdmin: boolean | undefined;

export function SettingsPage() {
  const { tab } = useParams();
  const { search, hash } = useLocation();
  const navigate = useNavigate();
  const { cloud } = useAuth();
  const standing = useCloudStanding();
  // The search belongs to the page it was typed on: a new page starts without it.
  const [typed, setTyped] = useState({ on: tab, query: '' });
  const query = typed.on === tab ? typed.query : '';
  const setQuery = (q: string) => setTyped({ on: tab, query: q });
  // An administrator: Manage on the organisation's settings, from what the person may do (GET /me/v1/permissions).
  const manages = useMay('organisation.settings', 'manage');
  // The earlier operator switches, read only by those who may change them (the API answers no one else).
  const roles = useLoad(() => (isCloud() || !manages ? Promise.resolve(undefined) : routes.roles().catch(() => undefined)), [manages]);
  // Every address change mounts the page afresh (App.tsx keys each page by its path): until the permissions answer
  // again, the last answer stands, so the nav does not drop the organisation's pages for a moment.
  const enterpriseAdmin = manages ?? lastEnterpriseAdmin ?? false;
  useEffect(() => {
    if (manages !== undefined) lastEnterpriseAdmin = manages;
  }, [manages]);
  const viewer: Viewer = {
    cloud: isCloud(),
    // On Cloud the session names the role; a development token has none and is treated as an administrator.
    admin: isCloud() ? (cloud?.organisation.role === 'owner' || cloud?.organisation.role === 'admin') : enterpriseAdmin,
    owner: cloud?.organisation.role === 'owner',
    known: isCloud() || manages !== undefined || lastEnterpriseAdmin !== undefined,
    standing,
    roles,
  };
  const moved = oldAddress(tab, search, hash);
  if (moved) return <Navigate to={moved} replace />;
  const section = SECTION_BY_KEY.get(tab ?? 'overview');
  if (!section) return <Navigate to="/settings" replace />;
  if (!section.shown(viewer)) return viewer.known ? <Navigate to="/settings" replace /> : null;

  const shown = SECTIONS.filter((s) => s.shown(viewer));
  const nav: SubnavGroup[] = (['', 'you', 'organisation', 'about'] as const)
    .map((g) => ({ label: GROUP_LABEL[g], items: shown.filter((s) => s.group === g).map((s) => ({ key: s.key, label: s.label, to: sectionPath(s.key) })) }))
    .filter((g) => g.items.length);
  const hits = searchSections(query, shown);
  const overview = section.key === 'overview';

  return (
    <SubnavLayout label="Settings" groups={nav} current={section.key}>
      <PageColumn width="form">
        <PageHeader
          tone={TONE}
          icon={overview ? <Gear /> : section.icon}
          title={overview ? 'Settings' : section.label}
          subtitle={section.blurb}
          actions={
            <SearchField
              aria-label="Search settings"
              variant="secondary"
              value={query}
              onChange={setQuery}
              onSubmit={() => {
                if (hits[0]) navigate(sectionPath(hits[0].key));
              }}
            >
              <SearchField.Group>
                <SearchField.SearchIcon />
                <SearchField.Input className="w-44" placeholder="Search settings…" />
                <SearchField.ClearButton />
              </SearchField.Group>
            </SearchField>
          }
        />
        {query.trim() ? (
          <ListGroup id="search" title="Matching pages">
            {hits.length ? (
              hits.map((s) => <ListRow key={s.key} to={sectionPath(s.key)} icon={s.icon} title={s.label} description={s.blurb} />)
            ) : (
              <ListBlock className="text-muted">Nothing in Settings matches “{query.trim()}”.</ListBlock>
            )}
          </ListGroup>
        ) : (
          <PartBoundary key={section.key} name={section.label} tone={TONE}>
            {section.render(viewer)}
          </PartBoundary>
        )}
      </PageColumn>
    </SubnavLayout>
  );
}

